// DateField — canonical `date` field primitive. Promoted off the CHG-1167
// _FieldStub onto the real FieldShell/useFieldIds/describedBy a11y pattern
// (DRIFT-910 slice 1, CHG-2457). Single `<input type="date">`. Storage: the
// native date string (`YYYY-MM-DD`, ISO-8601 date on the wire); emits `null`
// when cleared.
//
// Contract: specs/_product/field-primitive-registry.md §1 ('date') + §2.
// Doctrine: feedback_field_primitive_components_canonical.md.
// a11y (§2): label associated to the input via FieldShell <label htmlFor=id>;
// validation error in a role="alert" live region; aria-invalid + aria-required
// on the input; aria-describedby links the input to its error + helper text nodes.

import type { FieldPrimitiveProps } from "./types";
import { FieldShell, fieldControlStyle, useFieldIds, describedBy } from "./FieldShell";

export interface DateFieldProps extends FieldPrimitiveProps<string> {
  /** Optional min/max bounds forwarded to the native input. */
  min?: string;
  max?: string;
}

export function DateField({
  name,
  value,
  onChange,
  label,
  error,
  disabled,
  required,
  helperText,
  min,
  max,
}: DateFieldProps) {
  const ids = useFieldIds();
  return (
    <FieldShell
      htmlFor={ids.inputId}
      label={label}
      required={required}
      error={error}
      helperText={helperText}
      errorId={ids.errorId}
      helperId={ids.helperId}
    >
      {/* composition-lint-exempt: canonical v2 `date` primitive — this <input> IS the dispatch target (parallel to v1 components/forms/); rule 15's path-exemption omits the v2 components/fields/ canonical-owner dir (gate-aim gap, flagged for cross-team re-aim) (DRIFT-910) */}
      <input
        id={ids.inputId}
        name={name}
        type="date"
        value={value ?? ""}
        min={min}
        max={max}
        disabled={disabled}
        required={required}
        aria-invalid={error ? true : undefined}
        aria-required={required || undefined}
        aria-describedby={describedBy(ids, error, helperText)}
        onChange={(e) => onChange(e.target.value === "" ? null : e.target.value)}
        style={fieldControlStyle(Boolean(error))}
      />
    </FieldShell>
  );
}
